/**
 * Cloth that flows (lane C0-OUTFIT; user: "ให้พริ้วไหวเหมือนเกมกำลังภายใน"; research docs/research/C0_OUTFITS.md §7),
 * behind the setting ผ้าพลิ้ว (cloth.setting.ts, default on).
 *
 * The rig lets hair, capes, scarves, shawls, sash ends and tassels trail by `sk.rig.sway` (figure.ts `flutter`), but
 * each pose sets it frame by frame, so the cloth snaps with the body; and robe skirts are rigid. Here, per pose (a
 * 14-frame loop or one motion), a secondary-motion pass works out for every frame:
 * - `sway`: the pose's own sway, plus a breeze at rest and the body's and the weapon hand's speed, run through an
 *   underdamped spring so the cloth lags, overshoots and settles (follow-through);
 * - `hem`: the same through a slower, heavier spring: how far (art px, at the hem) robe skirts and skirts trail;
 * - `billow`: the hand's speed, eased: wide sleeves swing out on strikes.
 * Deterministic and cheap: worked out once per pose and body from the side-view skeletons (14 of them), then read per
 * frame; frames are drawn once and kept (workers, frameCache), so play costs nothing more.
 * `frameOf` (sets.ts) lays a frame's cloth on its skeleton (`withCloth`); `swayed` makes a skirt's rows trail.
 */
import { FRAMES, MOTIONS, type Pose } from '../sprites';
import { canvas, ctx2d } from '../pixel';
import type { Layer, Piece } from './figure';
import { type BodyShape, type Skeleton, skeleton } from './skeleton';

export interface Cloth {
  sway: number;
  hem: number;
  billow: number;
}

/** A skeleton carrying the cloth state of its frame. */
export type ClothSkeleton = Skeleton & { cloth?: Cloth };

export const clothOf = (sk: Skeleton): Cloth | undefined => (sk as ClothSkeleton).cloth;

/** The setting's value on this thread (cloth.setting.ts); a look's own `cloth` wins (workers get it there). */
let clothDefault = true;
export const setClothDefault = (on: boolean): void => {
  clothDefault = on;
};
export const clothDefaultOn = (): boolean => clothDefault;

const LOOPS: ReadonlySet<Pose> = new Set(['idle', 'walk', 'sprint', 'ready']);
const cache = new Map<string, Cloth[]>();

/** A damped spring toward `target`: stiffness `k`, damping `c` (low damping overshoots). */
function spring(targets: readonly number[], k: number, c: number, loop: boolean): number[] {
  const n = targets.length;
  let x = loop ? targets[0]! : 0;
  let v = 0;
  const out: number[] = new Array<number>(n).fill(0);
  // A loop runs three times so its start matches its end; a one-off motion starts from rest.
  const passes = loop ? 3 : 1;
  for (let p = 0; p < passes; p++)
    for (let f = 0; f < n; f++) {
      for (let s = 0; s < 4; s++) {
        v += (k * (targets[f]! - x) - c * v) * 0.25;
        x += v * 0.25;
      }
      if (p === passes - 1) out[f] = x;
    }
  return out;
}

/** The cloth over one pose's frames: `base` + 0…13 are the phases (a motion's block, or 0 for the loops). */
function cycle(pose: Pose, base: number, body: BodyShape): Cloth[] {
  const key = `${pose}:${base}:${JSON.stringify(body)}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const loop = LOOPS.has(pose);
  const idle = pose === 'motion' && String(MOTIONS[Math.floor(base / FRAMES)]?.id ?? '').startsWith('idl:');
  const sks = Array.from({ length: FRAMES }, (_, f) =>
    skeleton('side', pose, base + f, 0, body, false, null),
  );
  const at = (f: number) => sks[(f + FRAMES) % FRAMES]!;
  const target: number[] = [];
  const hand: number[] = [];
  for (let f = 0; f < FRAMES; f++) {
    const a = at(f);
    const b = f > 0 || loop ? at(f - 1) : a;
    // The body's speed forward (the side view faces +x) and the weapon hand's speed.
    const v = (a.joints.pelvis.x - b.joints.pelvis.x + a.joints.chest.x - b.joints.chest.x) / 2;
    const h = Math.hypot(a.joints.handA.x - b.joints.handA.x, a.joints.handA.y - b.joints.handA.y);
    hand.push(h);
    // At rest a breeze: a little lift, rising and falling over the loop.
    const breeze =
      pose === 'idle' || pose === 'ready' ? 0.25 + 0.3 * Math.sin((f / FRAMES) * Math.PI * 2) : 0;
    // A standing idle's hand gestures (motions/idles.ts) do not stir the robe and hair: only a strike's speed does.
    target.push(a.rig.sway + breeze + 0.9 * v + (idle ? 0 : 0.15 * h));
  }
  const sway = spring(target, 0.5, 0.45, loop);
  const hem = spring(
    target.map((t) => (t - 0.4) * 1.2),
    0.28,
    0.38,
    loop,
  );
  const billow = spring(hand, 0.6, 0.9, loop);
  const out = sway.map((s, f) => ({
    // Capped so the cloth never hides a wind-up: it trails behind, lifting at most about 50°.
    sway: Math.max(-1.5, Math.min(3.4, s)),
    hem: Math.max(-2, Math.min(5, hem[f]!)),
    billow: Math.max(0, Math.min(5, billow[f]!)),
  }));
  if (cache.size > 400) cache.clear();
  cache.set(key, out);
  return out;
}

/** The cloth of one frame, or none for poses it does not cover (single key poses: they keep the rig's own). */
export function clothAt(pose: Pose, ph: number, body: BodyShape): Cloth | undefined {
  if (LOOPS.has(pose)) return cycle(pose, 0, body)[((Math.round(ph) % FRAMES) + FRAMES) % FRAMES];
  if (pose !== 'motion') return undefined;
  const base = Math.floor(Math.round(ph) / FRAMES) * FRAMES;
  return cycle(pose, base, body)[Math.max(0, Math.min(FRAMES - 1, Math.round(ph) - base))];
}

/** The skeleton with the frame's cloth laid on (`lag`: fabric still settling from the last move, as skeleton.ts). */
export function withCloth(sk: Skeleton, c: Cloth, lag = 0): ClothSkeleton {
  return { ...sk, rig: { ...sk.rig, sway: c.sway + lag }, cloth: c };
}

/**
 * A layer whose skirt rows trail with the cloth: every row below the waist stretched toward the trailing side by
 * whole pixels, more toward the hem (no smear: rows are copied whole, pixels stay pixels; the leading edge stays on
 * the legs). Head-on, a small sideways stir instead. Without cloth on the skeleton, the layer as it was.
 */
export function swayed(layer: Layer): Layer {
  return {
    ...layer,
    pieces(sk, fit) {
      const pieces = layer.pieces(sk, fit);
      const c = clothOf(sk);
      if (!c) return pieces;
      const j = sk.joints;
      const top = j.waist.y + 1;
      const hemY = Math.max(j.ankleA.y, j.ankleB.y);
      const reach = sk.square ? Math.round(Math.sin(sk.phase * 0.9) * 0.7 * Math.min(1, c.hem)) : -c.hem;
      if (Math.round(reach) === 0 || hemY <= top) return pieces;
      const shift = (y: number) => {
        if (y <= top) return 0;
        const t = Math.min(1, (y - top) / (hemY - top));
        return Math.round(reach * Math.pow(t, 1.6));
      };
      return pieces.map((p): Piece => ({ ...p, draw: (g) => sheared(g, p, shift) }));
    },
  };
}

let scratch: CanvasRenderingContext2D | undefined;

function sheared(g: CanvasRenderingContext2D, p: Piece, shift: (y: number) => number): void {
  const { width: W, height: H } = g.canvas;
  if (!scratch || scratch.canvas.width !== W || scratch.canvas.height !== H) scratch = ctx2d(canvas(W, H));
  const s = scratch;
  const m = g.getTransform();
  s.setTransform(1, 0, 0, 1, 0, 0);
  s.clearRect(0, 0, W, H);
  s.setTransform(m);
  p.draw(s);
  g.save();
  g.setTransform(1, 0, 0, 1, 0, 0);
  for (let row = 0; row < H; row++) {
    // The canvas row back in the figure's drawing space (translation only: realism.ts `composeReal`).
    const dx = shift((row - m.f) / m.d) * m.a;
    // The row stretched toward the trailing side: its shifted copy under it.
    if (dx) g.drawImage(s.canvas, 0, row, W, 1, dx, row, W, 1);
    g.drawImage(s.canvas, 0, row, W, 1, 0, row, W, 1);
  }
  g.restore();
}
